import moment from 'moment';
import { LangType, ServerTimeType } from '../components/FilterCommon/types';
import { initialUpperCase } from '.';
import { getLocale } from 'umi';
import { getUnitValue, yAxleFormat } from './value';
import { hexToRgba } from './systemUtil';
import { getFilterDateQOQText } from './time';
import {
  channelInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import { dealWithOthersName } from './languageValue';

/**
 * echarts 名称过成自动换行（Y轴）通用
 * @param params
 * @returns
 */
export function chartLineFeed(params: any) {
  const newParamsName = params;
  const paramsNameNumber = params?.length;
  if (paramsNameNumber > 13) {
    return `${params?.substring(0, 13)}...`;
  }

  return newParamsName;
}

/**
 * 颜色渐变
 * @param value 传递参数
 * @returns
 */
export function gradual(value: number) {
  const four_five = [
    'rgb(120,218,164)',
    'rgb(109,209,149)',
    'rgb(98,201,134)',
    'rgb(87,193,119)',
    'rgb(76,185,104)',
    'rgb(65,177,89)',
    'rgb(54,168,74)',
    'rgb(43,160,59)',
    'rgb(32,152,44)',
    'rgb(21,144,29)',
  ];

  const one_tow = [
    'rgb(252,232,232)',
    'rgb(250,223,223)',
    'rgb(249,214,214)',
    'rgb(248,205,205)',
    'rgb(247,196,196)',
    'rgb(246,188,188)',
    'rgb(244,179,179)',
    'rgb(243,170,170)',
    'rgb(242,161,161)',
    'rgb(241,152,152)',
  ];

  const tow_three = [
    'rgb(240,144,144)',
    'rgb(232,146,146)',
    'rgb(225,149,149)',
    'rgb(218,151,151)',
    'rgb(211,154,154)',
    'rgb(204,156,156)',
    'rgb(197,159,159)',
    'rgb(190,161,161)',
    'rgb(183,164,164)',
    'rgb(176,166,166)',
  ];
  const three_four = [
    'rgb(169,169,169)',
    'rgb(164,173,168)',
    'rgb(159,178,168)',
    'rgb(154,183,167)',
    'rgb(149,188,167)',
    'rgb(144,193,166)',
    'rgb(139,198,166)',
    'rgb(134,203,165)',
    'rgb(129,208,165)',
    'rgb(124,213,164)',
  ];
  if (value < 1 || value == 1) {
    return '#FF0000';
  }
  if (value == 2) {
    return '#F09090';
  }
  if (value == 3) {
    return '#A9A9A9';
  }
  if (value == 4) {
    return '#78DAA4';
  }

  if (value == 5) {
    return '#15901D';
  }

  if (value > 1 && value < 2) {
    const index = value.toString().split('.')[1].charAt(0);
    return one_tow[index];
  }
  if (value > 2 && value < 3) {
    const index = value.toString().split('.')[1].charAt(0);

    return tow_three[index];
  }
  if (value > 3 && value < 4) {
    const index = value.toString().split('.')[1].charAt(0);
    return three_four[index];
  }
  if (value > 4 && value < 5) {
    const index = value.toString().split('.')[1].charAt(0);
    return four_five[index];
  }
}

/**
 * TODO: 其他系统使用
 * 格式化echart 的x轴(日期)
 * @param value
 * @param dateType
 * @returns
 */
export const formatTimeXAxisText = (
  value: string,
  dateType: ServerTimeType,
) => {
  if (dateType === 'monthly') {
    return moment(value).format('yyyy-MM');
  }
  if (dateType === 'hourly') {
    const tempValue = moment(value).format('MM-DD HH:mm:ss');
    if (tempValue.indexOf('00:00:00') !== -1) {
      return moment(value).format('MM-DD');
    }
    return moment(value).format('HH:mm');
  }
  return moment(value).format('MM-DD');
};

const OtherMap: any = {
  Others: { 'en-US': 'Others', 'zh-CN': '其他' },
  others: { 'en-US': 'Others', 'zh-CN': '其他' },
  other: { 'en-US': 'Others', 'zh-CN': '其他' },
  Other: { 'en-US': 'Others', 'zh-CN': '其他' },
  'unlabeled reviews': {
    'en-US': 'Unlabeled reviews',
    'zh-CN': '未标记的评论',
  },
  'Unlabeled reviews': {
    'en-US': 'Unlabeled reviews',
    'zh-CN': '未标记的评论',
  },
};

export function formatOtherInfo(
  value: string,
  func?: (value: string) => string,
) {
  const valueMap = OtherMap[value];
  const lang = getLocale();
  if (valueMap) return valueMap[lang];

  return func?.(value);
}

export const formatTextInfoFunc = (
  valueMap: { [key: string]: { 'en-US'?: string; 'zh-CN'?: string } } = {},
) => {
  const map = Object.assign({}, valueMap, OtherMap);
  return (key: string, lang: LangType) => {
    if (map?.[key]?.[lang]) {
      return map[key][lang];
    }
    return initialUpperCase(key);
  };
};

export const formatTextInfoSimpleFunc = (
  valueMap: { [key: string]: { 'en-US'?: string; 'zh-CN'?: string } } = {},
  lang: LangType = getLocale(),
) => {
  const map = Object.assign({}, valueMap, OtherMap);
  return (key: string) => {
    if (map?.[key]?.[lang]) {
      return map[key][lang];
    }
    return initialUpperCase(key);
  };
};

export const formatTextInfo = (
  key: string,
  lang: LangType,
  valueMap: { [key: string]: { 'en-US'?: string; 'zh-CN'?: string } } = {},
) => {
  const func = formatTextInfoFunc(valueMap);

  return func(key, lang);
};

/**
 * TODO: 其他系统使用
 * 获取图表浮窗的定位方法，该方法让图表浮窗固定底部位置，然后跟随鼠标的左右展示
 * @param pos            鼠标数值
 * @param params         tooltip 的positions参数
 * @param dom            tooltip 的positions参数
 * @param rect           tooltip 的positions参数
 * @param size           图表大小参数
 * @param bottom         下坐标数值
 * @param correctValue   偏差值，默认为15
 * @param positionKey    确定从什么位置作为参考值
 * @param leftDivisor    确定左右边界
 * @returns
 */
export function getChartPosition(
  pos: any,
  params: any,
  dom: any,
  rect: any,
  size: any,
  bottom = 0,
  correctValue = 15,
  positionKey: 'bottom' | 'top' = 'bottom',
  leftDivisor = 2,
) {
  // 鼠标在左侧时 tooltip 显示到右侧，鼠标在右侧时 tooltip 显示到左侧。
  const obj: any = { [positionKey]: bottom };
  const isLeft = pos[0] < size.viewSize[0] / leftDivisor;
  if (isLeft) {
    obj.left = pos[0] + correctValue;
  } else {
    obj.right = size.viewSize[0] - pos[0] + correctValue;
  }
  return obj;
}

const unitMap: any = {
  B: 1000000000,
  M: 1000000,
  K: 1000,
};

function maxValueFormate(max: number, splitNumber: number) {
  const [unit, value] = getUnitValue(max);
  const maxValue = Math.ceil(value) * (unitMap[unit] || 1);
  const interval = maxValue / splitNumber;

  return {
    interval,
    max: maxValue,
  };
}

function intervalValueFormate(max: number, splitNumber: number) {
  const interval = max / splitNumber;
  const [unit, value] = getUnitValue(interval);
  const intervalValue = Math.ceil(value) * (unitMap[unit] || 1);
  return {
    interval: intervalValue,
    max: intervalValue * splitNumber,
  };
}

// max小于1000就取段数的倍数，然后向上取整
function maxValueLittle(maxValue: number, splitNumber: number) {
  const multiple = Math.ceil(maxValue / splitNumber);
  const max = multiple * splitNumber;
  return {
    interval: multiple,
    max,
  };
}

export function getInterval(values: Array<number>, splitNumber = 5) {
  if (Array.isArray(values) && typeof values[0] === 'number') {
    const maxValue = Math.max(...values);

    if (maxValue < 1000) {
      return maxValueLittle(maxValue, splitNumber);
    }

    // 段值
    const splitValue = maxValue / splitNumber;
    if (splitValue < 1000) {
      // 段值小于1000，走最大值向上取整方案
      return maxValueFormate(maxValue, splitNumber);
    }
    // 段值大于1000，走段值向上取整方案
    return intervalValueFormate(maxValue, splitNumber);
  }

  return {
    max: 0,
    interval: 0,
  };
}

function getMinInterval(values: Array<number>) {
  if (Array.isArray(values) && typeof values[0] === 'number') {
    const maxValue = Math.max(...values);

    if (maxValue > 0.5 && maxValue < 1.1) {
      return {
        interval: 0.2,
        max: 1,
      };
    }

    if (maxValue > 0 && maxValue < 0.5) {
      return {
        interval: 0.1,
        max: 0.5,
      };
    }
  }

  return {
    max: 0,
    interval: 0,
  };
}
export function setMinIntervalValue(data: Array<number>, yAxisOption: any) {
  const { interval, max } = getMinInterval(data);
  if (interval) {
    yAxisOption.interval = interval;
    yAxisOption.max = max;
    yAxisOption.min = 0;
  }
}

export function setIntervalValue(data: Array<number>, yAxisOption: any) {
  const { interval, max } = getInterval(data);
  if (interval) {
    yAxisOption.interval = interval;
    yAxisOption.max = max;
    yAxisOption.min = 0;
  }
}

/**
 * TODO: 其他系统使用
 * echart 的series中areaStyle配置
 * @param color
 * @param startOpaque
 * @param endOpaque
 * @returns
 */
export function getChartsGradientColor(
  color: string,
  startOpaque: number,
  endOpaque: number,
) {
  const startColor = hexToRgba(color, startOpaque);
  const endColor = hexToRgba(color, endOpaque);

  return {
    color: {
      type: 'linear',
      x: 0,
      y: 1,
      x2: 0,
      y2: 0,
      colorStops: [
        {
          offset: 0,
          color: startColor,
        },
        {
          offset: 1,
          color: endColor,
        },
      ],
      global: false, // 缺省为 false
    },
  };
}

export const getXAxisAxisLabel = (filter: any) => {
  const { isSingleDate } = getFilterDateQOQText(filter);
  const { date_type, start_time } = filter;

  return {
    rich: {
      bold: {
        fontWeight: 'bold',
        color: '#000000', // 设置字体颜色
      },
      normal: {
        fontWeight: 'normal',
        color: '#838697', // 默认字体颜色
      },
    },
    formatter: (value: any) => {
      const time = formatTimeXAxisText(value, date_type);
      if (isSingleDate) {
        const currentTime = formatTimeXAxisText(start_time, date_type);
        if (currentTime === time) {
          return `{bold|${time}}`;
        }
      }
      return `{normal|${time}}`;
    },
  };
};

export const createChannelRichElement = (data: string[], corsMode = false) => {
  const rich =
    data?.reduce((acc: any, item: any) => {
      const { img } = channelInfo(item);
      let image: string | HTMLImageElement = img;
      if (corsMode) {
        const imgEl = new Image();
        imgEl.crossOrigin = 'anonymous';
        imgEl.src = img;
        image = imgEl;
      }
      acc[dealwithChannelChartKey(item)] = {
        width: 16,
        height: 16,
        backgroundColor: { image },
      };
      return acc;
    }, {}) || {};
  return rich;
};
export const getYAxisWithChannelIcon = ({
  data,
  type,
  formatMessage,
  corsMode = false,
}: {
  data: string[];
  type: string;
  formatMessage: any;
  corsMode?: boolean;
}) => {
  const measureTextWidth = (texts: string[], type = '') => {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    if (!context) {
      return [];
    }
    context.font = '12px Roboto';
    return texts?.map(
      text => context.measureText(dealWithOthersName(text, type)).width || 0,
    );
  };

  const maxLabelWidth = Math.max(...(measureTextWidth(data, type) || [])) || 60;
  const rounded = Math.round(maxLabelWidth / 10) * 10;
  const width = (rounded > maxLabelWidth ? rounded : rounded + 10) + 15;

  const rich = createChannelRichElement(data, corsMode);

  return [
    {
      type: 'category',
      position: 'left',
      axisLabel: {
        interval: 0,
        show: type.includes('channel'),
        formatter: (value: any) => `{${dealwithChannelChartKey(value)}|}`,
        rich,
        width: 20,
        align: 'right',
        padding: [0, width + 5, 0, 0],
      },
      splitLine: {
        show: false,
        lineStyle: { color: '#E5E5E5', type: 'dashed' },
      },
      axisLine: {
        show: false,
      },
      axisTick: {
        show: false,
      },
      data,
    },
    {
      type: 'category',
      position: 'left',
      axisLabel: {
        interval: 0,
        show: true,
        align: 'right',
        formatter: (value: any) => {
          let name = value;
          if (
            ['channel', 'language', 'region', 'country'].some(each =>
              type.includes(each),
            )
          ) {
            name = dealWithOthersName(value, type);
          } else {
            name = formatMessage({ id: value });
          }
          return `{label|${name}}`;
        },
        rich: {
          label: {
            align: 'left',
          },
        },
        padding: [0, 0, 0, 0],
        overflow: 'break',
        fontWeight: 400,
        fontSize: 12,
        lineHeight: 16,
        width,
      },
      splitLine: {
        show: false,
        lineStyle: { color: '#E5E5E5', type: 'dashed' },
      },
      axisLine: {
        show: true,
        lineStyle: { color: '#E5E5E5', type: 'solid' },
      },
      axisTick: {
        show: false,
      },
      data,
    },
  ];
};

/**
 * ECharts tooltip enterable 模式下的定位函数
 * 让 tooltip 始终显示在鼠标附近，方便用户移动进入浮窗（用于点击浮窗内按钮等交互场景）
 *
 * 水平方向定位（按优先级）：
 * - 配置了 horizontalSide 时：显示在鼠标左侧或右侧（默认带边界保护）
 * - 默认居中于鼠标（确保用户可以垂直向上进入）
 * - 设置 confine: false 可关闭边界保护，允许弹窗超出视图
 *
 * 垂直方向定位：
 * - 默认：显示在鼠标正上方
 * - 支持自定义固定位置（通过 config 参数配置）
 *
 * @param point - 鼠标位置 [x, y]
 * @param _params - tooltip 的参数（未使用）
 * @param _dom - tooltip 的 DOM 元素（未使用）
 * @param _rect - tooltip 的矩形信息（未使用）
 * @param size - 包含 contentSize（tooltip 尺寸）和 viewSize（视图尺寸）
 * @param config - 定位配置项（包含水平/垂直方向的各种配置）
 * @returns [x, y] - tooltip 的左上角坐标
 */
export function getTooltipEnterablePosition(
  point: TooltipPositionPoint,
  _params: any,
  _dom: HTMLElement,
  _rect: any,
  size: TooltipPositionSize,
  config?: TooltipPositionConfig,
): TooltipPositionPoint {
  const [mouseX] = point;
  const box = size.contentSize; // tooltip 的尺寸
  const viewWidth = size.viewSize[0]; // 视图宽度
  const viewHeight = size.viewSize[1]; // 视图高度
  const verticalGap = config?.verticalOffset ?? 10; // 默认 10px 的垂直间距
  const horizontalGap = config?.horizontalOffset ?? 10; // 默认 10px 的水平间距
  const shouldConfine = config?.confine !== false; // 默认限制在视图内

  // 水平方向：支持自定义固定位置
  let posX: number;

  if (config?.fixedX !== undefined) {
    // 使用固定 X 坐标
    posX = config.fixedX;
  } else if (config?.leftOffset !== undefined) {
    // 使用距离左侧的偏移量
    posX = config.leftOffset;
  } else if (config?.rightOffset !== undefined) {
    // 使用距离右侧的偏移量
    posX = viewWidth - box[0] - config.rightOffset;
  } else if (config?.horizontalSide === 'left') {
    // 弹窗显示在鼠标左侧（鼠标在弹窗右侧）
    posX = mouseX - box[0] - horizontalGap;
    if (shouldConfine) {
      // 左侧超出边界时，回退到鼠标右侧
      if (posX < 0) {
        posX = mouseX + horizontalGap;
        // 右侧也放不下，则贴左边界
        if (posX + box[0] > viewWidth) {
          posX = 10;
        }
      }
    }
  } else if (config?.horizontalSide === 'right') {
    // 弹窗显示在鼠标右侧（鼠标在弹窗左侧）
    posX = mouseX + horizontalGap;
    if (shouldConfine) {
      // 右侧超出边界时，回退到鼠标左侧
      if (posX + box[0] > viewWidth) {
        posX = mouseX - box[0] - horizontalGap;
        // 左侧也放不下，则贴右边界
        if (posX < 0) {
          posX = viewWidth - box[0] - 10;
        }
      }
    }
  } else {
    // 默认行为：居中于鼠标
    posX = mouseX - box[0] / 2;

    if (shouldConfine) {
      // 如果居中后右侧超出视图，向左移动避免被右侧菜单遮挡
      if (posX + box[0] > viewWidth) {
        posX = viewWidth - box[0] - 10;
      }
      // 如果左侧超出视图边界，向右移动
      else if (posX < 0) {
        posX = 10;
      }
    }
  }

  // 垂直方向：支持自定义固定位置
  let posY: number;

  if (config?.fixedY !== undefined) {
    // 使用固定 Y 坐标
    posY = config.fixedY;
  } else if (config?.topOffset !== undefined) {
    // 使用距离顶部的偏移量
    posY = config.topOffset;
  } else if (config?.bottomOffset !== undefined) {
    // 使用距离底部的偏移量
    posY = viewHeight - box[1] - config.bottomOffset;
  } else {
    // 默认行为：显示在鼠标正上方
    const mouseY = point[1];
    posY = mouseY - box[1] - verticalGap;

    if (shouldConfine) {
      // 只有当完全超出视图底部时，才显示在下方
      if (posY + box[1] > viewHeight) {
        posY = mouseY + verticalGap;
      }
    }
  }

  return [posX, posY];
}

/**
 * tooltip 可进入定位点的坐标类型
 */
export type TooltipPositionPoint = [number, number];

/**
 * tooltip 可进入定位的尺寸信息
 */
export interface TooltipPositionSize {
  contentSize: [number, number];
  viewSize: [number, number];
}

/**
 * tooltip 可进入定位的配置项
 */
export interface TooltipPositionConfig {
  /** 自定义固定 Y 坐标（优先级最高，设置后忽略鼠标 Y 坐标） */
  fixedY?: number;
  /** 距离视图顶部的偏移量（与 fixedY 二选一，设置后忽略鼠标 Y 坐标） */
  topOffset?: number;
  /** 距离视图底部的偏移量（与 fixedY/topOffset 二选一，设置后显示在底部固定位置） */
  bottomOffset?: number;
  /** tooltip 与鼠标之间的垂直距离（默认 10px），值越大 tooltip 离鼠标越远 */
  verticalOffset?: number;
  /** 自定义固定 X 坐标（优先级最高，设置后忽略鼠标 X 坐标和 horizontalSide） */
  fixedX?: number;
  /** 距离视图左侧的偏移量（与 fixedX 二选一，设置后忽略鼠标 X 坐标和 horizontalSide） */
  leftOffset?: number;
  /** 距离视图右侧的偏移量（与 fixedX/leftOffset 二选一，设置后忽略鼠标 X 坐标和 horizontalSide） */
  rightOffset?: number;
  /**
   * 弹窗显示在鼠标的哪一侧（仅在未设置 fixedX/leftOffset/rightOffset 时生效）
   * - 'left'：弹窗在鼠标左侧（鼠标在弹窗右侧）
   * - 'right'：弹窗在鼠标右侧（鼠标在弹窗左侧）
   * - 不设置（默认）：弹窗水平居中于鼠标
   */
  horizontalSide?: 'left' | 'right';
  /** tooltip 与鼠标之间的水平距离（默认 10px），仅在 horizontalSide 为非默认时生效 */
  horizontalOffset?: number;
  /**
   * 是否将 tooltip 限制在视图范围内（默认 true）
   * - true：自动调整位置避免超出视图边界
   * - false：允许弹窗超出边界，始终保持在鼠标指定侧
   */
  confine?: boolean;
}

/**
 * 生成预配置的 tooltip 可进入定位函数（工厂函数）
 *
 * 由于 ECharts 的 tooltip.position 回调签名固定，无法额外传入 config，
 * 通过此工厂函数可以预先绑定配置，返回可直接赋给 tooltip.position 的函数。
 *
 * @param config - tooltip 定位配置项
 * @returns 符合 ECharts tooltip position 回调签名的定位函数
 *
 * @example
 * ```ts
 * // 弹窗显示在鼠标上方，水平居中
 * tooltip: {
 *   enterable: true,
 *   position: getTooltipEnterablePositionFunc({ verticalOffset: 20 }),
 * }
 *
 * // 弹窗始终显示在鼠标右侧，允许超出边界
 * tooltip: {
 *   enterable: true,
 *   position: getTooltipEnterablePositionFunc({ horizontalSide: 'right', confine: false }),
 * }
 * ```
 */
export function getTooltipEnterablePositionFunc(
  config?: TooltipPositionConfig,
): (
    point: TooltipPositionPoint,
    _params: any,
    dom: HTMLElement,
    _rect: any,
    size: TooltipPositionSize,
  ) => TooltipPositionPoint {
  return (point, _params, dom, _rect, size) =>
    getTooltipEnterablePosition(point, _params, dom, _rect, size, config);
}

/**
 * 对数字进行特定规则的舍入计算
 * @param x 输入的数字
 * @returns 计算结果
 */
export function specialRound(x: number): number {
  // 1. 先对x取整（去掉小数部分）
  const integerX = Math.floor(x);
  // 将数字转为字符串以获取长度
  const str = integerX.toString();
  // 计算10的(length-1)次方
  const power = Math.pow(10, str.length - 1);
  // 执行计算：向上取整(x / power) * power
  return Math.ceil(x / power) * power;
}

export function getSentimentColor(value: number) {
  if (value > 3.5) return '#69D19C';
  if (value >= 2.5) return '#838697';
  return '#F17C86';
}

/**
 * 获取真实的 X 轴值（处理不同日期类型的格式转换）
 * 用于图表事件时间轴数据展示
 *
 * @param xAxis - 原始 X 轴值
 * @param dateType - 日期类型 (daily/weekly/monthly/hourly)
 * @param format - 目标格式字符串
 * @param xAxisData - X 轴数据数组（用于 weekly 类型计算）
 * @returns 格式化后的 X 轴值
 */
export function getRealXAxisValue({
  xAxis,
  dateType,
  format,
  xAxisData = [],
}: {
  xAxis: string;
  dateType: any;
  format?: string;
  xAxisData: Array<string>;
}) {
  let realXAxisValue = xAxis;
  if (dateType === 'monthly') {
    realXAxisValue = moment(xAxis, 'YYYY-MM').format(format);
  } else if (dateType === 'hourly') {
    realXAxisValue = moment(xAxis, 'YYYY-MM-DD HH').format(format);
  } else if (dateType === 'daily') {
    realXAxisValue = moment(xAxis).format(format);
  } else if (dateType === 'weekly') {
    const xAxisMoment = moment(xAxis);
    for (const date of xAxisData || []) {
      const weekStart = moment(date);
      const weekEnd = weekStart.clone().add(6, 'days');

      if (xAxisMoment.isBetween(weekStart, weekEnd, 'day', '[]')) {
        const newTime = weekStart.format(format);
        realXAxisValue = format ? date : newTime;
        break;
      }
    }
  }

  return realXAxisValue;
}
